import { Drawer as AntDrawer } from 'antd'
import type { CSSProperties } from 'react'
import { MdOutlineArrowDownward } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'

import { statusConfirmation } from './statusConfirmation'
import { ACTION_ICONS } from './TaskActionButtons'
import { TASK_STATUS_COLORS, type TaskAction } from './taskStatus'

import styles from './ConfirmStatusSheet.module.css'

interface ConfirmStatusSheetProps {
    /** Дія, яку водій обрав; null — шторка закрита */
    action: TaskAction | null
    taskId: number | string
    currentStatus: string
    /** confirmedAt — момент натискання «Так, підтвердити» (ISO), іде в аудит */
    onConfirm: (action: TaskAction, confirmedAt: string) => void
    onCancel: () => void
}

/**
 * Підтвердження зміни статусу завдання водієм: «було → стане» і дві великі кнопки.
 * Статус змінюється лише після «Так, підтвердити»; закриття шторки будь-яким способом — скасування.
 */
export const ConfirmStatusSheet = ({ action, taskId, currentStatus, onConfirm, onCancel }: ConfirmStatusSheetProps) => {
    const confirmation = action ? statusConfirmation(action, currentStatus) : null

    return (
        <AntDrawer
            open={action !== null}
            onClose={onCancel}
            placement="bottom"
            height="auto"
            closable={false}
            title={null}
            styles={{
                wrapper: { background: 'transparent', boxShadow: 'none' },
                section: { background: 'var(--pwa-surface)', borderRadius: '18px 18px 0 0' },
                body: { padding: 0 },
            }}
        >
            {action && confirmation && (
                <div className={cx(styles, 'sheet')} role="alertdialog" aria-labelledby="confirm-status-question">
                    <span className={cx(styles, 'grab')} aria-hidden />
                    <span className="pwa-label">Завдання #{taskId}</span>
                    <h2 id="confirm-status-question" className={cx(styles, 'question')}>
                        {confirmation.question}
                    </h2>
                    {confirmation.hint && <p className={cx(styles, 'hint')}>{confirmation.hint}</p>}

                    <div className={cx(styles, 'transition')}>
                        <div
                            className={[cx(styles, 'plate'), cx(styles, 'plate--from')].join(' ')}
                            style={{ '--status': TASK_STATUS_COLORS[currentStatus] ?? '#8c8c8c' } as CSSProperties}
                        >
                            <span className={cx(styles, 'plateTag')}>Зараз</span>
                            <span className={cx(styles, 'plateLabel')}>{confirmation.fromLabel}</span>
                        </div>
                        <span className={cx(styles, 'arrow')} aria-hidden>
                            <MdOutlineArrowDownward />
                        </span>
                        <div
                            className={[cx(styles, 'plate'), cx(styles, 'plate--to')].join(' ')}
                            style={{ '--status': TASK_STATUS_COLORS[action.next] ?? '#8c8c8c' } as CSSProperties}
                        >
                            <span className={cx(styles, 'plateTag')}>Стане</span>
                            <span className={cx(styles, 'plateLabel')}>
                                <span className={cx(styles, 'plateIcon')} aria-hidden>
                                    {ACTION_ICONS[action.icon]}
                                </span>
                                {confirmation.toLabel}
                            </span>
                        </div>
                    </div>

                    <div className={cx(styles, 'buttons')}>
                        <button
                            type="button"
                            className="pwa-btn pwa-btn--primary pwa-btn--block"
                            data-testid="confirm-status"
                            onClick={() => onConfirm(action, new Date().toISOString())}
                        >
                            Так, підтвердити
                        </button>
                        <button type="button" className="pwa-btn pwa-btn--secondary pwa-btn--block" onClick={onCancel}>
                            Скасувати
                        </button>
                    </div>
                </div>
            )}
        </AntDrawer>
    )
}
